import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import { Badge, Button, Card, Input, Label } from "../../components/ui-kit";
import { Brain, Megaphone, PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";

const initial = {
  title: "Promo Nexora",
  message: "Profitez d'une reduction sur votre prochaine course Nexora Cab.",
  audience: "customers",
  channels: ["push", "email"],
  promo_code: "NEXORA10",
  discount_percent: 10,
  poster_prompt: "",
};

export default function AdminAds() {
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/ad-campaigns")
    .then(({ data }) => setCampaigns(data))
    .catch((e) => toast.error(formatApiError(e)))
    .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const toggleChannel = (key) => {
    setForm((current) => ({
      ...current,
      channels: current.channels.includes(key)
        ? current.channels.filter((item) => item !== key)
        : [...current.channels, key],
    }));
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/ad-campaigns", form);
      toast.success("Publicite creee");
      setForm(initial);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const aiSuggest = async () => {
    setSaving(true);
    try {
      await api.post("/admin/ad-campaigns/ai-suggest");
      toast.success("Proposition IA creee, en attente d'approbation");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const approve = async (id) => {
    try {
      await api.post(`/admin/ad-campaigns/${id}/approve`, { approved: true });
      toast.success("Campagne approuvee");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const publish = async (id) => {
    try {
      const { data } = await api.post(`/admin/ad-campaigns/${id}/publish`);
      toast.success(`Publiee: ${data.delivery.targets} cible(s)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e), { duration: 9000 });
    }
  };

  return (
    <AdminShell title="Publicites">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter sm:text-4xl">Publicites et promotions</h1>
          <p className="text-sm text-muted-foreground">Creer des ads, generer des affiches et notifier email, WhatsApp et push.</p>
        </div>
        <Button onClick={aiSuggest} disabled={saving}><Brain weight="fill" /> Proposer par IA</Button>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 xl:grid-cols-[420px_1fr]">
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <Megaphone size={22} weight="fill" className="text-primary" />
            <div className="font-heading text-xl font-black">Nouvelle pub</div>
          </div>
          <form onSubmit={create} className="space-y-3">
            <div><Label>Titre</Label><Input value={form.title} onChange={(e) => change("title", e.target.value)} required /></div>
            <div>
              <Label>Message</Label>
              <textarea
                value={form.message}
                onChange={(e) => change("message", e.target.value)}
                rows={4}
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-foreground outline-none focus:border-primary"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Audience</Label>
                <select value={form.audience} onChange={(e) => change("audience", e.target.value)} className="w-full rounded-xl border border-white/10 bg-background px-4 py-3">
                  <option value="customers">Clients</option>
                  <option value="drivers">Chauffeurs</option>
                  <option value="all">Tous</option>
                </select>
              </div>
              <div><Label>Reduction %</Label><Input type="number" min="1" max="90" value={form.discount_percent} onChange={(e) => change("discount_percent", Number(e.target.value))} /></div>
            </div>
            <div><Label>Code promo</Label><Input value={form.promo_code} onChange={(e) => change("promo_code", e.target.value.toUpperCase())} /></div>
            <div>
              <Label>Canaux</Label>
              <div className="grid grid-cols-3 gap-2">
                {["push", "email", "whatsapp"].map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => toggleChannel(c)}
                    className={`rounded-xl px-3 py-2 text-[10px] font-bold uppercase ${form.channels.includes(c) ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <Button type="submit" className="w-full" disabled={saving}>Creer la campagne</Button>
          </form>
        </Card>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {loading && <Card className="p-8 text-center text-sm font-bold uppercase tracking-widest text-muted-foreground">Chargement...</Card>}
          {campaigns.map((c) => (
            <Card key={c.id} className="overflow-hidden">
              <div className="bg-black p-2">
                {c.poster_svg ? (
                  <div className="aspect-[4/5] overflow-hidden rounded-xl bg-black" dangerouslySetInnerHTML={{ __html: c.poster_svg }} />
                ) : (
                  <div className="flex aspect-[4/5] items-center justify-center rounded-xl bg-white/5 text-muted-foreground">Affiche</div>
                )}
              </div>
              <div className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-heading text-lg font-black">{c.title}</div>
                    <div className="text-xs text-muted-foreground">{c.message}</div>
                  </div>
                  <Badge tone={c.status === "PUBLISHED" ? "success" : c.status === "AI_PENDING_APPROVAL" ? "warning" : "primary"}>{c.status}</Badge>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge tone="primary">{c.audience}</Badge>
                  <Badge tone="success">{c.promo_code}</Badge>
                  <Badge>{c.discount_percent}%</Badge>
                  {c.channels?.map((ch) => <Badge key={ch}>{ch}</Badge>)}
                </div>
                {c.delivery && (
                  <div className="rounded-xl bg-white/5 p-3 text-xs text-muted-foreground">
                    Cibles {c.delivery.targets} · push {c.delivery.push} · email {c.delivery.email} · WhatsApp {c.delivery.whatsapp}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => approve(c.id)} disabled={c.status === "PUBLISHED"}>
                    <Sparkle weight="fill" /> Approuver
                  </Button>
                  <Button onClick={() => publish(c.id)} disabled={c.status === "PUBLISHED"}>
                    <PaperPlaneTilt weight="fill" /> Publier
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}
